import type {
  User,
  UserAchievement,
  UserBadge,
  AchievementId,
  LinkedAuthProvidersResponse,
  AuthResponse,
  ApiResponse,
  ArchiveEntry,
  ArchivePixelData,
  Comment,
  BugReport,
  BugReportStatus,
  CreateBugReportRequest,
  AdminRole,
  LeaderboardEntry,
  CanvasExpansionHistoryEntry,
} from '@the-wall/shared';

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public retryAfterSeconds?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = (await response.json()) as ApiResponse<T> & { retryAfterSeconds?: number };

  if (!response.ok || !data.success) {
    const retryAfterSeconds = response.status === 429 ? data.retryAfterSeconds : undefined;
    throw new ApiError(data.error ?? 'Request failed', response.status, retryAfterSeconds);
  }

  return data.data as T;
}

async function requestBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
    },
  });

  if (!response.ok) {
    let message = 'Request failed';
    try {
      const data = (await response.json()) as ApiResponse<never>;
      message = data.error ?? message;
    } catch {
      // Non-JSON response; keep generic message.
    }
    throw new ApiError(message, response.status);
  }

  return response.blob();
}

function authHeader(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, password: string, username: string) =>
    request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, username }),
    }),

  getCurrentUser: (token: string) =>
    request<User>('/auth/me', {
      headers: authHeader(token),
    }),

  getAuthProviders: (token: string) =>
    request<LinkedAuthProvidersResponse>('/auth/providers', {
      headers: authHeader(token),
    }),

  sendVerificationEmail: (token: string) =>
    request<{ message: string }>('/auth/send-verification', {
      method: 'POST',
      headers: authHeader(token),
    }),

  requestPasswordReset: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, newPassword: string) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),

  updateProfile: (
    token: string,
    data: { profilePicture?: string | null; usernameColor?: string | null }
  ) =>
    request<User>('/auth/profile', {
      method: 'PUT',
      headers: authHeader(token),
      body: JSON.stringify(data),
    }),

  getAchievements: (token: string) =>
    request<{ achievements: UserAchievement[]; selectedBadges: UserBadge[] }>(
      '/auth/achievements',
      {
        headers: authHeader(token),
      }
    ),

  updateSelectedBadges: (token: string, achievementIds: AchievementId[]) =>
    request<{ selectedBadges: UserBadge[]; user: User }>('/auth/achievements/selected', {
      method: 'PUT',
      headers: authHeader(token),
      body: JSON.stringify({ achievementIds }),
    }),

  getUserBadges: (userId: string) =>
    request<{ badges: UserBadge[] }>(`/users/${encodeURIComponent(userId)}/badges`),

  // Gallery (pass token so admins see hidden archives and can hide/unhide)
  getArchives: (token?: string | null) =>
    request<ArchiveEntry[]>('/gallery', {
      ...(token ? { headers: authHeader(token) } : {}),
    }),

  getArchive: (id: string, token?: string | null) =>
    request<ArchiveEntry>(`/gallery/${id}`, {
      ...(token ? { headers: authHeader(token) } : {}),
    }),

  hideArchive: (token: string, archiveId: string, hidden: boolean) =>
    request<{ hidden: boolean }>(`/gallery/${archiveId}/hide`, {
      method: 'PATCH',
      headers: authHeader(token),
      body: JSON.stringify({ hidden }),
    }),

  getArchivePixels: (id: string, token?: string | null) =>
    request<ArchivePixelData>(`/gallery/${id}/pixels`, {
      ...(token ? { headers: authHeader(token) } : {}),
    }),

  // Leaderboard (no auth required)
  getLeaderboard: (period: 'daily' | 'alltime', limit?: number) => {
    const params = new URLSearchParams({ period });
    if (limit != null) params.set('limit', String(limit));
    return request<LeaderboardEntry[]>(`/canvas/leaderboard?${params}`);
  },

  // Canvas expansion history (no auth required)
  getCanvasExpansions: () => request<CanvasExpansionHistoryEntry[]>('/canvas/expansions'),

  getArchiveComments: (archiveId: string, token?: string | null) =>
    request<Comment[]>(`/gallery/${archiveId}/comments`, {
      ...(token ? { headers: authHeader(token) } : {}),
    }),

  voteComment: (token: string, commentId: string, vote: 1 | -1) =>
    request<{ upvotes: number; downvotes: number; userVote: 1 | -1 | null }>(
      `/gallery/comments/${commentId}/vote`,
      {
        method: 'POST',
        headers: authHeader(token),
        body: JSON.stringify({ vote }),
      }
    ),

  createComment: (token: string, archiveId: string, content: string) =>
    request<Comment>(`/gallery/${archiveId}/comments`, {
      method: 'POST',
      headers: authHeader(token),
      body: JSON.stringify({ content }),
    }),

  deleteComment: (token: string, commentId: string) =>
    request<{ deleted: boolean }>(`/gallery/comments/${commentId}`, {
      method: 'DELETE',
      headers: authHeader(token),
    }),

  // Bug Reports
  createBugReport: (token: string, data: CreateBugReportRequest) =>
    request<BugReport>('/bug-reports', {
      method: 'POST',
      headers: authHeader(token),
      body: JSON.stringify(data),
    }),

  // Admin
  listUsers: (token: string, page: number = 1, pageSize: number = 20, search?: string) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) params.set('search', search);

    return request<UserListResponse>(`/admin/users?${params}`, {
      headers: authHeader(token),
    });
  },

  updateUserRole: (token: string, userId: string, role: AdminRole) =>
    request<User>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: authHeader(token),
      body: JSON.stringify({ role }),
    }),

  /** Timeout user for 1 hour (cannot paint or comment). */
  timeoutUser: (token: string, userId: string) =>
    request<User>(`/admin/users/${userId}/timeout`, {
      method: 'POST',
      headers: authHeader(token),
    }),

  /** Set whether user is hidden from the leaderboard. */
  setUserLeaderboardVisibility: (token: string, userId: string, hidden: boolean) =>
    request<User>(`/admin/users/${userId}/leaderboard-visibility`, {
      method: 'PUT',
      headers: authHeader(token),
      body: JSON.stringify({ hidden }),
    }),

  /** Delete all chat messages from this user sent in the last hour. */
  deleteUserChatMessagesLastHour: (token: string, userId: string) =>
    request<{ deletedCount: number; messageIds: string[] }>(
      `/admin/users/${userId}/chat-messages/delete-last-hour`,
      {
        method: 'POST',
        headers: authHeader(token),
      }
    ),

  completeUserAchievement: (token: string, userId: string, achievementId: AchievementId) =>
    request<{ achievementId: AchievementId }>(`/admin/users/${userId}/achievements/complete`, {
      method: 'POST',
      headers: authHeader(token),
      body: JSON.stringify({ achievementId }),
    }),

  listBugReports: (token: string, status: BugReportStatus | 'all' = 'open') =>
    request<BugReport[]>(`/admin/bug-reports?status=${encodeURIComponent(status)}`, {
      headers: authHeader(token),
    }),

  getBugReportScreenshot: (token: string, reportId: string) =>
    requestBlob(`/admin/bug-reports/${encodeURIComponent(reportId)}/screenshot`, {
      headers: authHeader(token),
    }),

  updateBugReportStatus: (token: string, reportId: string, status: BugReportStatus) =>
    request<BugReport>(`/admin/bug-reports/${encodeURIComponent(reportId)}/status`, {
      method: 'PATCH',
      headers: authHeader(token),
      body: JSON.stringify({ status }),
    }),
};
